import React, { useMemo, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { listPageDialogPaperProps } from '../theme/darkDialog';

type DeleteConfirmOptions = {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
};

export function useDeleteConfirmation() {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<DeleteConfirmOptions>({
    title: 'Confirm delete',
    message: '',
    confirmLabel: 'Delete',
    cancelLabel: 'Cancel',
  });
  const resolver = useRef<((confirmed: boolean) => void) | null>(null);

  const confirmDelete = (opts: DeleteConfirmOptions): Promise<boolean> => {
    setOptions({
      title: opts.title ?? 'Confirm delete',
      message: opts.message,
      confirmLabel: opts.confirmLabel ?? 'Delete',
      cancelLabel: opts.cancelLabel ?? 'Cancel',
    });
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  };

  const closeWithResult = (confirmed: boolean) => {
    setOpen(false);
    resolver.current?.(confirmed);
    resolver.current = null;
  };

  const dialog = useMemo(
    () => (
      <Dialog open={open} onClose={() => closeWithResult(false)} maxWidth="xs" fullWidth PaperProps={listPageDialogPaperProps}>
        <DialogTitle>{options.title}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'inherit' }}>{options.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => closeWithResult(false)} variant="outlined">
            {options.cancelLabel}
          </Button>
          <Button onClick={() => closeWithResult(true)} variant="contained" color="error">
            {options.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    ),
    [open, options]
  );

  return { confirmDelete, deleteConfirmationDialog: dialog };
}
